import React from 'react';
import { UtilityService } from '../../common/services/utility.service';
import { GeolocationHelper } from '../../common/address-to-geoloc';
import { SceneManager } from '../../common/scene.manager';
import { MapService } from '../../common/services/map.service';
import { ServiceManager } from '../../services/service-manager';
import { BuildingHelperService } from '../../app-confi/services/building-helper.service';
import { useCallback, useEffect, useState } from 'react';
import { Building, Project, Site } from '../../confi-state';
import ProjectConf from '../ProjectConf/ProjectConf';
import SiteConf from '../SiteConf/SiteConf';
import BuildingConf from '../BuildingConf/BuildingConf';
import { BabylonService } from '../BuildingConf/babylon.service';
import Babylon from '../Babylon/Babylon';
import { workspaceEnum } from '../../models/workspace.enum';
import BuildingInfo from '../BuildingInfo/BuildingInfo';
import SiteInfo from '../SiteInfo/SiteInfo';
import { usePrevious } from '../../common/react-helpers';
import { buildingType } from '../../components/BuildingConf/models/building-type.enum';
import { accuracyEnum } from '../../common/models/accuracy.enum';
import DocumentationView from '../DocumentationView/DocumentationView';
import BackdropSpinner from '../BackdropSpinner/BackdropSpinner';
import { SvgAssets } from '../../common/models/svg';
import { Button, IconButton, Grid, Tooltip } from '@mui/material';
import { Description, DownloadOutlined, UploadOutlined } from '@mui/icons-material';
import { HttpService } from '../../common/services/http.service';
import { Spinner } from '../../models/spinner';
import { Features } from '../../models/features';
import { FirestoreService } from '../../common/firebase/firestore.service';
import { firestoreCollections } from '../../services/firestore-collections.enum';
import { severity } from '../../common/models/severity.enum';
import { Polygon } from '../../common/models/polygon';
import { FlatPolygon } from '../../common/models/flat-polygon';
import { environment } from '../../environment.enum';
import { setEnvKey } from '../../environments';
import { CloudStorageService } from '../../common/firebase/cloud-storage.service';
import { collection, doc, getFirestore, serverTimestamp, writeBatch } from 'firebase/firestore';
import User from '../../common/authentication/User/User';
import { nlCartiqoLayersEnum } from '../../common/models/nl-cartiqo-layers.enum';
// import useKeypress from 'react-use-keypress';
import { Column } from '../BuildingConf/models/column';
import { Part, RefinedParts } from '../BuildingConf/models/part';
import WishChart from '../WishChart/WishChart';
import { Point3 } from '../../common/models/point.type';
import { activeStateEnum, initialPropsStepEnum, InitProperties } from '../BuildingConf/Common/BuildingInitialPropertiesDialog/init-props';
import BuildingInitialPropertiesDialog from '../BuildingConf/Common/BuildingInitialPropertiesDialog/BuildingInitialPropertiesDialog';
import { SectionJSON } from '../../models/section-json';
import { UnitWish } from '../../models/unit-wish-list';
import NavigationComponent, { NavLevel } from '../Common/NavigationComponent/NavigationComponent';
import { NavItem } from '../Common/NavigationComponent/NavItems/NavItems';
import { navActionEnum } from '../Common/NavigationComponent/NavActions/NavActions';
import styles from './Confi.module.css';
import { explanationIds } from '../../common/authentication/models/user';
import ProjectDialog from '../Common/ProjectDialog/ProjectDialog';
import SiteDialog from '../Common/SiteDialog/SiteDialog';
import { config } from 'process';
import { ContactPerson, Environment } from '../../environment';
import DocumentationSelection from '../DocumentationSelection/DocumentationSelection';
import DocumentationPage from '../DocumentationPage/DocumentationPage';

enum drawStateEnum {
    building = 'building',
    site = 'site',
    project = 'project',
}


const getProjects = async (userId: string): Promise<Project[]> => {
    const projects = await FirestoreService.getCollectionForUserId<Project>(firestoreCollections.projects, userId,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van projekten: "${error.message}"`, severity.error);
        });
    return projects ?? [];
}

const getBuildings = async (userId: string, projectId: number | string, siteId: number | string): Promise<Building[]> => {
    const buildings = await FirestoreService.getCollectionForUserId<Building>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, userId,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens het laden van gebouwen: "${error.message}"`, severity.error);
        });
    ServiceManager.babylonService.markBuildingsAsDeprecated(buildings);
    ServiceManager.babylonService.mapPartsForBuildings(buildings);
    return buildings ?? [];
}

const getSites = async (userId: number | string, projectId: number | string): Promise<Site[]> => {
    let sites = await FirestoreService.getCollectionForUserId<Site>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}`, userId as any,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van kavels: "${error.message}"`, severity.error);
        });
    if (sites && sites.length > 0) {
        sites = sites.map(s => {
            if (s.border && s.border.polygons && s.border.polygons.length > 0) {
                s.border.polygons = ServiceManager.utilityService.inflateFlatPolygons(s.border.polygons as FlatPolygon[]);
            }
            return s;
        })
    }
    return sites ?? [];
}


const Confi = ({ user }) => {

    const [initialised, setInitialised] = useState<boolean>(false);
    const [section, setSection] = useState<SectionJSON>(undefined);
    const [projects, setProjects] = useState<Project[]>([]);
    const [sites, setSites] = useState<Site[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [currentProject, setCurrentProject] = useState<Project>(undefined);
    const [currentSite, setCurrentSite] = useState<Site>(undefined);
    const [currentBuilding, setCurrentBuilding] = useState<Building>(undefined);
    const [drawState, setDrawState] = useState<drawStateEnum>(drawStateEnum.project);
    const [workspace, setWorkspace] = useState<workspaceEnum>(workspaceEnum.babylon);
    const [buildingScaledDimensions, setBuildingScaleDim] = useState<Point3>([1, 1, 1]);
    const [accuracy, setAccuracy] = useState(accuracyEnum.conceptual);
    const [spinner, setSpinner] = useState<Spinner[]>([]);
    const [pickedContextItem, setPickedContextItem] = useState<{ id: string, type: nlCartiqoLayersEnum }>(undefined);
    const [hoveredContextItem, setHoveredContextItem] = useState<{ id: string }>(undefined);
    const [loadedFeatures, setLoadedFeatures] = useState<Features>({
        buildings: [],
        trees: [],
        parcels: [],
        zones: [],
        dimensions: [],
    });
    const [editingUnderlay, setEditingUnderlay] = useState(false);
    const [changingContextVisibility, setChangingContextVisibility] = useState(false);
    const [drawingParcel, setDrawingParcel] = useState(false);
    const [pickingParcels, setPickingParcels] = useState(false);
    const [buildingInitProps, setBuildingInitProps] = useState<InitProperties>({
        type: buildingType.hybrid,
        unitWishList: [],
        stepState: initialPropsStepEnum.buildingType,
        state: activeStateEnum.add
    });
    const [openBuildingTypeModal, setOpenBuildingTypeModal] = useState(false);
    const [openNewProjectModal, setOpenNewProjectModal] = useState(false);
    const [projectName, setProjectName] = useState<string>(undefined);
    const [openNewSiteModal, setOpenNewSiteModal] = useState(false);
    const [siteAddress, setSiteAddress] = useState<{ address: string }>(undefined);
    const [contactPerson, setContactPerson] = useState<ContactPerson>(undefined);

    const [previousBuildingDimensions, setPreviousBuildingDimensions] = useState<Point3>([0, 0, 0]);
    const [navLevels, setNavLevels] = useState<NavLevel[]>([]);

    const [svgAssets, setSvgAssets] = useState<SvgAssets>({
        assets: [],
        plans: [],
        frontViews: []
    });

    const previousProject = usePrevious<Project>(currentProject);
    const previousSite = usePrevious<Site>(currentSite);
    const previousBuildings = usePrevious<Building[]>(buildings);
    const previousAccuracy = usePrevious<accuracyEnum>(accuracy);

    const addSpinner = (id: string, message: string) => {
        // update only if id is not yet present
        setSpinner((prevValue) => {
            return [...prevValue, { id, message }]
        });
    }

    const removeSpinner = (id: string) => {
        // update only if id is present
        setSpinner((prevValue) => {
            return prevValue.filter(s => s.id !== id);
        });
    }

    const fetchSection = async () => {
        const res = await CloudStorageService.downloadFile('section.json', (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van bestand section.json: "${error.message}"`, severity.error);
        });
        const data = await res.json();
        setSection(data);
    };

    const fetchConfig = async () => {
        const config: Environment = await FirestoreService.getDoc(firestoreCollections.config, process.env.REACT_APP_ENVIRONMENT as environment,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van configuratie settings: "${error.message}"`, severity.error);
            });
        if (config.contactPerson) {
            setContactPerson(config.contactPerson);
        }
        if (config) {
            Object.keys(config).forEach(key => {
                setEnvKey(process.env.REACT_APP_ENVIRONMENT as environment, key, config[key]);
            })
        }
    }

    // CRUD Projects


    const addProject = async (project: Project) => {
        ServiceManager.babylonService.cleanScene();
        const s = await FirestoreService.addDoc(firestoreCollections.projects, { ...project, userId: user.uid },
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens toevoegen van het project: "${error.message}"`, severity.error);
            });
        const p = { ...project, id: s.id, userId: user.uid };
        setProjects([...projects, p]);
        setCurrentProject({ ...p });
        return p;
    }

    const editProject = async (project) => {
        await FirestoreService.editDoc(firestoreCollections.projects, project, project.id,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens wijzigen van het project: "${error.message}"`, severity.error);
            });
        setProjects(projects.map(p => p.id === project.id ? { ...project } : p));
        setCurrentProject({ ...project });
    }

    const archiveProject = async (id: number | string) => {
        ServiceManager.babylonService.cleanScene();
        const docRef = await FirestoreService.archiveDoc(firestoreCollections.projects, id as string,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens archiveren van het project: "${error.message}"`, severity.error);
            });

        if (docRef) {
            const remainingProjects = projects.filter(s => s.id !== id);
            if (remainingProjects.length > 0) {
                setCurrentProject({ ...remainingProjects[remainingProjects.length - 1] });
            } else {
                setCurrentProject(undefined);
            }
            setProjects(remainingProjects);
        }
    }

    // CRUD Sites

    const addSite = async (projectId: number | string, site: Site, updateState = true) => {
        ServiceManager.babylonService.cleanScene();
        if (updateState) {
            setCurrentSite(undefined);
        }
        site.userId = user.uid;
        site.projectId = projectId;
        let siteResult;
        const data = await FirestoreService.addDoc<Site>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}`, site,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens toevoegen van de kavel: "${error.message}"`, severity.error);
            });
        if (updateState) {
            const newSite = { ...site, id: data.id };
            const allSites = [...sites, newSite];
            setSites(allSites);
            setCurrentSite({ ...newSite });
        }
        return siteResult;
    }

    const editSite = async (projectId: number | string, site: Site) => {
        const s = { ...site };

        // send flattened polygons to db as firestore does not accept nested arrays
        if (s.border && s.border.polygons && s.border.polygons.length > 0) {
            s.border = { ...site.border };
            s.border.polygons = ServiceManager.utilityService.flattenPolygons(s.border.polygons as Polygon[], 3);
        }

        await FirestoreService.editDoc<Site>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}`, s, s.id as string,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens wijzigen van de kavel: "${error.message}"`, severity.error);
            });

        if (site.underlayImage && (site.underlayImage as any)._methodName === 'deleteField') {
            delete site.underlayImage;
        }

        if (sites.length === 1 && site.lon && site.lat) {
            ServiceManager.explanationService.showExplanation(explanationIds.initialDisplay);
        }

        setSites(sites.map(s => s.id === site.id ? { ...site } : s));
        setCurrentSite({ ...site });
    }

    const archiveSite = async (projectId: number | string, id: number | string) => {
        ServiceManager.babylonService.cleanScene();
        await FirestoreService.archiveDoc(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}`, id as string,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens archiveren van de kavel: "${error.message}"`, severity.error);
            });

        const remainingSites = sites.filter(s => s.id !== id);
        if (remainingSites.length > 0) {
            setCurrentSite({ ...remainingSites[remainingSites.length - 1] });
        } else {
            setCurrentSite(undefined);
        }
        setSites(remainingSites);
    }

    // CRUD Buildings

    const copyBuilding = async (projectId: number | string, siteId: number | string, buildingToCopy?: Building) => {
        const building: Building = {};
        prepareNewBuilding(building, user.uid, currentBuilding.type, projectId, siteId);
        const b = await FirestoreService.addDoc<Building>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, building,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens toevoegen van het gebouw: "${error.message}"`, severity.error);
            });
        buildingToCopy.name = `${buildingToCopy.name} ${buildings.length + 1}`;
        buildingToCopy = ServiceManager.buildingHelperService.cloneBuilding(buildingToCopy);
        buildingToCopy.locationInSite.position[0] = buildingToCopy.locationInSite.position[0] + 20;
        buildingToCopy.locationInSite.position[2] = buildingToCopy.locationInSite.position[2] + 20;
        await editBuilding(buildingToCopy.projectId, buildingToCopy.siteId, { ...buildingToCopy, id: b.id });
        if (b) {
            const newBuilding = { ...buildingToCopy, id: b.id };
            setBuildings([...buildings, newBuilding]);
            setCurrentBuilding(newBuilding);
        }
    }

    const addBuilding = async (projectId: number | string, siteId: number | string, type: buildingType, building?: Building, updateState = true) => {
        if (updateState) {
            setCurrentBuilding(undefined);
        }
        if (!building) {
            building = {};
            prepareNewBuilding(building, user.uid, type, projectId, siteId);
        } else {
            building.projectId = projectId;
            building.siteId = siteId;
        }
        const data = await FirestoreService.addDoc<Building>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, building,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens toevoegen van het gebouw: "${error.message}"`, severity.error);
            });
        if (updateState && data) {
            const newBuilding = { ...building, id: data.id };
            setBuildings([...buildings, newBuilding]);
            setCurrentBuilding(newBuilding);
        }
    }

    const editBuilding = useCallback(async (projectId: number | string, siteId: number | string, building: Building, saveToDb = true) => {
        let clonedParts = [];
        if (building.props && building.props.parts) {
            clonedParts = building.props.parts.map((p: Part<any, any>) => {
                const columns = p.columns.map((c: Column<any, any, any, any>) => {
                    const col = { ...c }
                    delete col.unitsAndAmenities;
                    return col;
                })
                return { ...p, columns }
            })
        }

        let clonedRefinedParts: RefinedParts<any, any>[] = [];
        if (building.props && building.props.partsRefined) {
            clonedRefinedParts = building.props.partsRefined.map((pr: RefinedParts<any, any>) => {
                const parts = pr.parts.map((p: Part<any, any>) => {
                    const columns = p.columns.map((c: Column<any, any, any, any>) => {
                        const col = { ...c }
                        delete col.unitsAndAmenities;
                        return col;
                    })
                    return { ...p, columns }
                })
                return { id: pr.id, parts };
            })
        }

        const b: Building = { ...building, props: { ...building.props, parts: clonedParts, partsRefined: clonedRefinedParts } };
        // delete units and amenities from the parts object as it holds derivative data, which can always be computed from primary column definitions

        if (saveToDb) {
            FirestoreService.editDoc<Building>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, b, b.id as string,
                (error) => {
                    ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens aanpassen van het gebouw: "${error.message}"`, severity.error);
                });
        }
        setBuildings(buildings.map(b => b.id === building.id ? { ...building } : b));
        setCurrentBuilding({ ...building });
    }, [buildings]);

    const archiveBuilding = async (projectId: number | string, siteId: number | string, id: number | string) => {
        await FirestoreService.archiveDoc(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, id as string,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens archiveren van het gebouw: "${error.message}"`, severity.error);
            });
        const remainingBuildings = buildings.filter(s => s.id !== id && !s.deprecated);
        if (remainingBuildings.length > 0) {
            setCurrentBuilding({ ...remainingBuildings[remainingBuildings.length - 1] });
        } else {
            setCurrentBuilding(undefined);
        }
        setBuildings(remainingBuildings);
        ServiceManager.babylonService.remove3DBuilding(id);
    }

    const saveProject = () => {
        const buildingsInSitesPromises = sites.map(s => {
            return getBuildings(user.uid, s.projectId, s.id);
        })
        Promise.all(buildingsInSitesPromises).then(siteBuildings => {
            const siteBuildingsFlat = siteBuildings.flat();
            const project = ServiceManager.buildingHelperService.includeAdditionalData(currentProject, sites, siteBuildingsFlat);
            let proj = JSON.stringify(project);
            if (currentProject) {
                ServiceManager.utilityService.download(currentProject.name + " " + currentProject.id, proj);
            } else {
                ServiceManager.snackbarService.openSnackbar('Er zijn geen projecten om te downloaden.', severity.error, 3000)
            }
        })
    }

    const importProject = (e) => {
        e.preventDefault();
        const input = document.getElementById('import-project') as HTMLInputElement;
        input.onchange = (e: any) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                const file = files[0];
                file.text().then(text => {
                    const projDescription: {
                        project: Project,
                        sites: Site[],
                        buildings: Building[]
                    } = JSON.parse(text);

                    let newProjName = ServiceManager.utilityService.recurseNameSearch(projDescription.project.name, projects.map(p => p.name), 1);

                    const createProjectWithSitesAndBuildings = async () => {
                        const id = 'projectImport';
                        addSpinner(id, 'Project wordt geimporteerd...');
                        try {
                            const db = getFirestore();

                            const batch = writeBatch(db);
                            const newProjectRef = doc(collection(db, "projects"));
                            delete projDescription.project.id;
                            const project = { ...projDescription.project, name: newProjName, userId: user.uid, archived: false };
                            batch.set(newProjectRef, { ...project, lastEdit: serverTimestamp() });
                            if (projDescription.sites && projDescription.sites.length > 0) {
                                projDescription.sites.forEach(s => {
                                    let buildingsInTheSite = [];
                                    if (projDescription.buildings) {
                                        buildingsInTheSite = projDescription.buildings.filter(b => b.siteId === s.id);
                                    }

                                    if (s.border && s.border.polygons && s.border.polygons.length > 0) {
                                        s.border.polygons = ServiceManager.utilityService.flattenPolygons(s.border.polygons as Polygon[], 3);
                                    }

                                    const newSiteRef = doc(collection(db, `projects/${newProjectRef.id}/sites`));
                                    delete s.id;
                                    const siteToAdd = { ...s, userId: user.uid, projectId: newProjectRef.id, archived: false, lastEdit: serverTimestamp() };
                                    batch.set(newSiteRef, siteToAdd);
                                    buildingsInTheSite.forEach(b => {
                                        const newBuildingRef = doc(collection(db, `projects/${newProjectRef.id}/sites/${newSiteRef.id}/buildings`));
                                        delete b.id;
                                        const buildingToAdd = { ...b, userId: user.uid, projectId: newProjectRef.id, siteId: newSiteRef.id, archived: false, lastEdit: serverTimestamp() };
                                        batch.set(newBuildingRef, buildingToAdd);
                                    });
                                })
                            }
                            await batch.commit();
                            const p = { ...project, id: newProjectRef.id, userId: user.uid };
                            setProjects([...projects, p]);
                            setCurrentProject({ ...p });
                            removeSpinner(id);
                        } catch (e) {
                            if (e && e.message) {
                                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens importeren van project: "${e.message}"`, severity.error);
                            } else {
                                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens importeren van project`, severity.error);
                            }

                            removeSpinner(id);
                        }
                    }

                    createProjectWithSitesAndBuildings();
                });
            }
        }
        input.click();
    }

    const updatePositionAndRotation = useCallback((res) => {
        const building = buildings.find(b => b.id === res.groupId);
        if (building) {
            const b = { ...building };
            b.locationInSite = {
                position: [res.position[0], 0, res.position[2]],
                rotation: res.rotation
            }
            editBuilding(b.projectId, b.siteId, b);
        }
    }, [buildings, editBuilding]);

    useEffect(() => {
        const projectItems = projects.map(p => ({ value: p.id, title: p.name, active: p.id === currentProject?.id }));
        const siteItems = sites.map(p => ({ value: p.id, title: p.name, active: p.id === currentSite?.id }));
        const buildingItems = buildings.map(p => ({ value: p.id, title: p.name, active: p.id === currentBuilding?.id }))

        let projectsActive = projectItems.length && navLevels ? navLevels.find(l => l.title === 'Project')?.active : false;
        let sitesActive = siteItems.length && navLevels ? navLevels.find(l => l.title === 'Kavel')?.active : false;
        let buildingsActive = buildingItems.length && navLevels ? navLevels.find(l => l.title === 'Gebouw')?.active : false

        if (!projectsActive && projectItems.length !== 0 && siteItems.length === 0) {
            projectsActive = true;
            sitesActive = false;
            buildingsActive = false;
        }

        if (!sitesActive && siteItems.length > 0 && buildingItems.length === 0) {
            projectsActive = false;
            sitesActive = true;
            buildingsActive = false;
        }

        if (!buildingsActive && buildingItems.length) {
            projectsActive = false;
            sitesActive = false;
            buildingsActive = true;
        }

        setNavLevels([
            {
                title: 'Project',
                items: projectItems,
                active: projectsActive,
                actionsAllowed: [navActionEnum.add, navActionEnum.edit, navActionEnum.archive]
            },
            {
                title: 'Kavel',
                items: siteItems,
                active: sitesActive,
                actionsAllowed: [navActionEnum.add, navActionEnum.edit, navActionEnum.archive]
            },
            {
                title: 'Gebouw',
                items: buildingItems,
                active: buildingsActive,
                actionsAllowed: [navActionEnum.add, navActionEnum.edit, navActionEnum.duplicate, navActionEnum.archive]
            }
        ])
    }, [projects, sites, buildings, currentBuilding, currentProject, currentSite])

    useEffect(() => {
        return () => {
            if (ServiceManager.sceneManager) {
                ServiceManager.sceneManager.disposeScene();
                setBuildings([]);
                setProjects([]);
                setSites([]);
                setCurrentBuilding(undefined);
            }
        }
    }, [])

    useEffect(() => {
        if (currentBuilding && currentBuilding.refined) {
            ServiceManager.sceneManager.modelLoaderService.disableBoundingBoxGizmo(currentBuilding.id);
        } else if (currentBuilding && !currentBuilding.refined) {
            ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(currentBuilding.id);
        }
    }, [currentBuilding]);

    useEffect(() => {
        if (buildingInitProps.stepState === initialPropsStepEnum.finished) {
            if (buildingInitProps.state === activeStateEnum.edit) {
                const cb = { ...currentBuilding };
                if (cb.props) {
                    setPreviousBuildingDimensions([cb.props.buildingLength, cb.props.buildingHeight, cb.props.buildingWidth])
                }
                cb.type = buildingInitProps.type;
                cb.props = undefined;
                cb.refined = false;
                cb.selectedForDoc = false;
                cb.floorsForDoc = [];
                cb.deprecated = false;

                ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(cb.id);
                editBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...cb })
            }
            else if (buildingInitProps.state === activeStateEnum.editUnitWishList) {
                const cb = { ...currentBuilding, props: currentBuilding.props };
                (cb.props as { unitWishList: UnitWish[] }).unitWishList = buildingInitProps.unitWishList.map(u => ({ type: u.type, uiName: u.uiName, percentage: u.percentage }));
                editBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...cb })
            }
            else {
                addBuilding(currentProject.id, currentSite.id, buildingInitProps.type);
                setDrawState(drawStateEnum.building);
            }
            setOpenBuildingTypeModal(false);
        }
    }, [buildingInitProps.stepState])

    const handleLevelItem = (level: NavLevel, item: NavItem, action: navActionEnum) => {

        switch (level.title) {
            case 'Gebouw':
                if (action === navActionEnum.toggle) {
                    setCurrentBuilding(undefined);
                    setCurrentBuilding({ ...buildings.find(b => b.id === item.value) });
                } else if (action === navActionEnum.add) {
                    setBuildingInitProps({ type: buildingType.singleLoadedCorridor, unitWishList: [], stepState: initialPropsStepEnum.buildingType, state: activeStateEnum.add });
                    setOpenBuildingTypeModal(true);
                } else if (action === navActionEnum.edit) {
                    setDrawState(drawStateEnum.building);
                } else if (action === navActionEnum.archive) {
                    archiveBuilding(currentProject.id, currentSite.id, currentBuilding.id)
                } else if (action === navActionEnum.duplicate) {
                    copyBuilding(currentProject.id, currentSite.id, currentBuilding);
                    setDrawState(drawStateEnum.building);
                }
                break;
            case 'Kavel':
                if (action === navActionEnum.toggle) {
                    setCurrentSite(undefined);
                    setCurrentSite({ ...sites.find(s => s.id === item.value) });
                } else if (action === navActionEnum.add) {
                    setDrawState(drawStateEnum.project);
                    setOpenNewSiteModal(true);
                } else if (action === navActionEnum.edit) {
                    setDrawState(drawStateEnum.site);
                } else if (action === navActionEnum.archive) {
                    archiveSite(currentProject.id, currentSite.id)
                } else if (action === navActionEnum.duplicate) {
                    // TODO
                }
                break;
            case 'Project':
                if (action === navActionEnum.toggle) {
                    setCurrentProject(undefined);
                    setCurrentProject({ ...projects.find(p => p.id === item.value) });
                } else if (action === navActionEnum.add) {
                    setOpenNewProjectModal(true);
                } else if (action === navActionEnum.edit) {
                    setDrawState(drawStateEnum.project)
                } else if (action === navActionEnum.archive) {
                    archiveProject(currentProject.id)
                } else if (action === navActionEnum.duplicate) {

                }
                break;
            default:
                alert('unknown level provided');
                break;
        }
    }

    const pickObject = useCallback((res: string | number) => {
        if (currentBuilding && res !== currentBuilding.id && !editingUnderlay && !pickingParcels && !drawingParcel) {
            setCurrentBuilding({ ...buildings.find(b => b.id === res) });
            setDrawState(drawStateEnum.building);
        } else if (buildings.length > 0 && !editingUnderlay && !pickingParcels && !drawingParcel) {
            setDrawState(drawStateEnum.building);
        }

        if (currentBuilding && res && drawState === drawStateEnum.building) {
            setTimeout(() => {
                ServiceManager.explanationService.showExplanation(explanationIds.buildingEdit3d);
            }, 500)
        }
    }, [currentBuilding, buildings, editingUnderlay, pickingParcels, drawingParcel]);

    const buildingScaleChanged = useCallback((res) => {
        if (res.groupId === currentBuilding.id) {
            const dim = ServiceManager.babylonService.sceneManager.modelLoaderService.getGizmoBoxDimensions(currentBuilding.id, false);
            const roundToTwo = (num): number => {
                return Math.round(num * 100) / 100;
            }
            const size: Point3 = [roundToTwo(dim[0]), roundToTwo(dim[1]), roundToTwo(dim[2])];
            setBuildingScaleDim(size);
        }
    }, [currentBuilding]);

    const pickedContextObject = (res) => {
        setPickedContextItem({ ...res });
    };

    const hoverContextObject = (res) => {
        setHoveredContextItem(res);
    };

    const positionRotationScaleImageTileChanged = (res: { position: Point3, rotation: Point3, scale: number }) => {
        editSite(currentSite.projectId, { ...currentSite, underlayImage: { ...currentSite.underlayImage, position: res.position, rotation: res.rotation, scale: res.scale } })
    };

    if (ServiceManager.babylonService && ServiceManager.babylonService.sceneManager) {
        ServiceManager.babylonService.sceneManager.posOrRotOfGroupChangedCallback = updatePositionAndRotation;
        ServiceManager.babylonService.sceneManager.pickedGroupCallback = pickObject;
        ServiceManager.babylonService.sceneManager.scaleChangedCallback = buildingScaleChanged;
        ServiceManager.babylonService.sceneManager.pickedContextItemCallback = pickedContextObject;
        ServiceManager.babylonService.sceneManager.hoverContextItemCallback = hoverContextObject;

        if (ServiceManager.sceneManager.imageTileService) {
            ServiceManager.sceneManager.imageTileService.positionRotationScaleChanged = positionRotationScaleImageTileChanged;
        }

        if (ServiceManager.babylonService.sceneManager.modelLoaderService) {
            ServiceManager.babylonService.sceneManager.modelLoaderService.posOrRotOfGroupChangedCallback = updatePositionAndRotation;
            ServiceManager.babylonService.sceneManager.modelLoaderService.pickedGroupCallback = pickObject;
            ServiceManager.babylonService.sceneManager.modelLoaderService.scaleChangedCallback = buildingScaleChanged;
        }
    }

    useEffect(() => {
        if (!initialised) {
            setInitialised(true);
        }
    }, [initialised])

    useEffect(() => {
        if (initialised) {
            const utilityService = new UtilityService();
            const geolocationHelper = new GeolocationHelper();
            const sceneManager = new SceneManager(utilityService, geolocationHelper);
            const mapService = new MapService();
            const buildingHelperService = new BuildingHelperService(utilityService);
            const babylonService = new BabylonService(sceneManager, buildingHelperService, mapService, utilityService);
            const httpService = new HttpService();

            ServiceManager.initServices(babylonService, sceneManager, buildingHelperService, utilityService, httpService);

            const loadInitialData = async () => {
                const id = 'loadingInitialData';
                addSpinner(id, 'Initiele gegevens worden opgehaald...');

                await fetchSection();
                if (process.env.REACT_APP_ENVIRONMENT === environment.development || process.env.REACT_APP_ENVIRONMENT === environment.production || process.env.REACT_APP_ENVIRONMENT === environment.local) {
                    await fetchConfig();
                }
                const p = await getProjects(user.uid,);
                if (p && p.length > 0) {
                    const pr = p[p.length - 1];
                    setProjects(p);
                    setCurrentProject(pr);
                    const s = await getSites(pr.userId, pr.id);
                    if (s && s.length > 0) {
                        const si = s[s.length - 1];
                        setSites(s);
                        setCurrentSite(si);
                        const b = await getBuildings(user.uid, si.projectId, si.id);
                        if (b && b.length > 0) {
                            const bu = b[b.length - 1];
                            setBuildings(b);
                            setCurrentBuilding(bu);
                        }
                        removeSpinner(id);
                    } else {
                        setOpenNewSiteModal(true);
                        removeSpinner(id);
                    }
                } else {
                    setOpenNewProjectModal(true);
                    removeSpinner(id);
                }
            }
            loadInitialData();
        }
    }, [initialised, user.uid])

    useEffect(() => {
        if (initialised && accuracy && accuracy !== previousAccuracy) {
            const id = 'renderingBuildings';
            addSpinner(id, 'Gebouwen worden geladen...');
            const buildingsRenderable = buildings.filter(s => s.props && s.props.parts);
            buildings.forEach(b => {
                ServiceManager.babylonService.remove3DBuilding(b.id);
            })
            const promises = ServiceManager.babylonService.renderBuildings(buildingsRenderable, accuracy);
            Promise.all(promises).then(s => {
                removeSpinner(id);
            })
        }
    }, [initialised, buildings, accuracy, previousAccuracy])

    useEffect(() => {
        if (initialised) {
            if (!currentProject) {
                setSites([]);
                setBuildings([]);
                setCurrentSite(undefined);
                setCurrentBuilding(undefined);
            } else if (!previousProject || currentProject.id !== previousProject.id) {
                const gs = async () => {
                    const id = 'loadingProject';
                    addSpinner(id, 'Project data wordt opgehaald...');
                    const s = await getSites(currentProject.userId, currentProject.id);
                    const si = s[s.length - 1];
                    setSites(s);
                    setCurrentSite(si);
                    removeSpinner(id);
                }
                gs();
            }
        }
    }, [initialised, currentProject, previousProject])

    useEffect(() => {
        if (initialised) {
            if (!currentSite && previousBuildings && buildings.length !== 0) {
                setBuildings([]);
                setCurrentBuilding(undefined);
                ServiceManager.babylonService.cleanScene();
            } else if ((currentSite && !previousSite) || (currentSite && currentSite.id !== previousSite.id)) {
                const gb = async () => {

                    const id = 'loadingInitialMap';
                    addSpinner(id, 'Omgeving wordt geladen...');

                    const promises = [];

                    const b = await getBuildings(user.uid, currentSite.projectId, currentSite.id);
                    let buildingsRenderable;
                    if (b && b.length > 0) {
                        const bu = b[b.length - 1];
                        setBuildings(b);

                        setCurrentBuilding(bu);
                        buildingsRenderable = b.filter(s => s.id !== bu.id && s.props && s.props.parts);
                        ServiceManager.babylonService.cleanScene();

                        if (buildingsRenderable.length > 0) {
                            const renderBuildingsPromises = ServiceManager.babylonService.renderBuildings(buildingsRenderable, accuracy);
                            promises.push(...renderBuildingsPromises);
                        }
                    } else if (b.length === 0) {
                        setBuildings([]);
                        setCurrentBuilding(undefined);

                        ServiceManager.babylonService.cleanScene();
                    }

                    if (currentSite.lat && currentSite.lon) {
                        const promiseSite = ServiceManager.babylonService.loadSite(currentSite.lon, currentSite.lat, currentSite?.border?.parcels, currentSite.context, currentSite.options);
                        promises.push(promiseSite);
                    }

                    if (currentSite.underlayImage) {
                        const ui = currentSite.underlayImage;
                        ServiceManager.sceneManager.imageTileService.setImageUrlTile(ui.link, ui.position, ui.rotation, ui.width, ui.height, ui.scale);
                        if (currentSite.options && !currentSite.options.underlayVisible) {
                            ServiceManager.sceneManager.imageTileService.hideImageTile();
                        }
                    }
                    ServiceManager.babylonService.sceneManager.cleanPolyline();

                    if (currentSite.border && currentSite.border.polygons && currentSite.border.polygons.length === 1 && (!currentSite.border.parcels || currentSite.border.parcels.length === 0)) {
                        ServiceManager.babylonService.sceneManager.showPolyline((currentSite.border.polygons[0] as Polygon).path);
                    }

                    if (promises.length > 0) {
                        Promise.all(promises).then((res) => {
                            res.forEach(r => {
                                if (r && r.features) {
                                    setLoadedFeatures(r.features);
                                }
                            })

                            if (buildingsRenderable && buildingsRenderable.length > 0) {
                                buildingsRenderable.forEach(b => {
                                    if (b && b.refined) {
                                        ServiceManager.sceneManager.modelLoaderService.disableBoundingBoxGizmo(b.id);
                                    } else if (b && !b.refined) {
                                        ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(b.id);
                                    }
                                })
                            }

                            removeSpinner(id);
                        })
                    } else {
                        removeSpinner(id);
                    }

                }
                gb();
            }
        }
    }, [initialised, user, accuracy, currentSite, previousSite, previousBuildings, buildings.length])

    useEffect(() => {
        if (projectName) {
            addProject({ name: projectName });
            setDrawState(drawStateEnum.project);
            setOpenNewSiteModal(true);
        }
    }, [projectName]);

    useEffect(() => {
        if (siteAddress) {
            addSite(currentProject.id, { name: siteAddress.address, geoSearch: { query: siteAddress.address } });
            setDrawState(drawStateEnum.site);
        }
    }, [siteAddress]);

    const prepareNewBuilding = (building: Building, userId: string, type: buildingType, projectId: string | number, siteId: string | number) => {
        building.name = 'Gebouw ' + (ServiceManager.buildingHelperService.getHighestNameNr(buildings.map(s => s.name)) + 1);
        building.userId = userId;
        building.projectId = projectId;
        building.siteId = siteId;
        building.type = type;
        building.locationInSite = {
            position: [0, 0, 0],
            rotation: [0, 0, 0]
        };
    }

    return user && section ? (
        <div data-testid="Confi" className="screen">
            <BackdropSpinner spinnerState={spinner}></BackdropSpinner>
            <div id="map">
            </div>
            <div className={`${workspace === workspaceEnum.documentation && "hidden"}`}>
                <Babylon
                    currentBuilding={currentBuilding}
                    currentSite={currentSite}
                    user={user}
                    contactPerson={contactPerson}
                    pickedContextItem={pickedContextItem}
                    changingContextVisibility={changingContextVisibility}
                    onChangeBuilding={editBuilding}
                    onChangeSite={editSite}
                    section={section}
                    accuracy={accuracy}
                    workspace={workspace}
                    pickingParcels={pickingParcels}
                    drawingParcel={drawingParcel}
                    editingUnderlay={editingUnderlay}
                    onChangeAccuracy={setAccuracy}
                    setChangingContextVisibility={setChangingContextVisibility}
                ></Babylon>
            </div>
            <div className="left">
                <div className="errors">
                </div>
                <div className="header" data-testid="header">
                    <a className={styles.moosLogo} target="_blank" rel="noreferrer" href="https://wijzijnmoos.nl/"><img className={styles.logoImage} alt="Logo of In the middle of our street BV" src="/assets/mooslogo.svg"></img></a>
                    <Grid container spacing={2}>
                        {currentProject?.name &&
                            <Grid item xs={6}>
                                <h1 className={styles.projectName}>
                                    {currentProject.name}
                                </h1>
                            </Grid>
                        }
                        <Grid item xs={6}>
                            {currentProject &&
                                <>
                                    <Tooltip title="Project Documenteren">
                                        <IconButton color="secondary" data-testid="document-project-btn" aria-label="document picture" component="span" onClick={(e) => {
                                            if (workspace !== workspaceEnum.documentation) {
                                                setWorkspace(workspaceEnum.documentation)
                                            } else {
                                                setWorkspace(workspaceEnum.babylon);
                                                setTimeout(() => {
                                                    ServiceManager.sceneManager.restoreDefaultCamera();
                                                })
                                            }
                                        }
                                        }>
                                            <Description />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Project Exporteren">
                                        <IconButton color="secondary" data-testid="save-project" aria-label="project opslaan" component="span" onClick={() => saveProject()}>
                                            <DownloadOutlined />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            }
                            <Tooltip title="Project Importeren">
                                <IconButton color="secondary" data-testid="import-project-btn" aria-label="upload picture" component="span" onClick={(e) => importProject(e)}>
                                    <UploadOutlined />
                                </IconButton>
                            </Tooltip>

                            <input data-testid="file-input" type="file" accept=".json" id="import-project" />
                        </Grid>

                    </Grid>
                </div>
                <div className={workspace === workspaceEnum.babylon ? "group" : "group hidden"}>
                    {
                        !pickingParcels && !drawingParcel && !editingUnderlay &&
                        <div>
                            <NavigationComponent levels={navLevels} setLevels={setNavLevels}
                                toggleLevelItem={(level, item, action) => handleLevelItem(level, item, action)}
                            />
                            <>
                                {(!projects || projects.length === 0) &&
                                    <Button data-testid="add-new-project" color="primary" variant="contained" onClick={() => {
                                        setOpenNewProjectModal(true);
                                    }
                                    }>Project toevoegen</Button>
                                }
                            </>
                            <>
                                {(projects && projects.length > 0 && currentProject && (!sites || sites.length === 0)) &&
                                    <Button color="primary" variant="contained" data-testid="add-new-site" onClick={() => {
                                        setDrawState(drawStateEnum.project);
                                        setOpenNewSiteModal(true);
                                    }}>
                                        Kavel toevoegen
                                    </Button>}
                            </>
                            <>
                                {currentSite && currentSite.lon && currentSite.lat && (!buildings || buildings.length === 0) &&
                                    <Button data-explanation-id={explanationIds.buildingAdd} autoFocus={true} color="primary"
                                        variant="contained"
                                        data-testid="add-new-building"
                                        onClick={() => {
                                            setBuildingInitProps({ type: buildingType.singleLoadedCorridor, unitWishList: [], stepState: initialPropsStepEnum.buildingType, state: activeStateEnum.add });
                                            setOpenBuildingTypeModal(true);
                                            setTimeout(() => {
                                                ServiceManager.explanationService.showExplanation(explanationIds.buildingSelectType);
                                            }, 500)
                                        }}>
                                        Gebouw toevoegen
                                    </Button>
                                }
                            </>
                        </div>
                    }
                    <div>
                        {
                            currentBuilding && <BuildingInfo currentBuilding={currentBuilding}></BuildingInfo>
                        }
                        {
                            currentSite && <SiteInfo currentSite={currentSite} buildings={buildings}></SiteInfo>
                        }
                    </div>
                </div>
                <div className={workspace === workspaceEnum.documentation ? "group" : "group hidden"}>
                    <DocumentationSelection buildings={buildings} onChangeBuilding={editBuilding}></DocumentationSelection>
                </div>
            </div>
            {workspace === workspaceEnum.documentation &&
                <div className="documentation-workspace">
                    <DocumentationPage
                        currentProject={currentProject} currentSite={currentSite} buildings={buildings} section={section}
                        addSpinner={addSpinner} removeSpinner={removeSpinner}
                        svgAssets={svgAssets} onChangeSvgAssets={setSvgAssets} />
                </div>
            }
            <div className={`center ${workspace === workspaceEnum.documentation && "hidden"}`}>
                <div className={styles.attribution}><a target="_blank" rel="noreferrer" href="https://maptiler.com">© MapTiler</a> <a target="_blank" rel="noreferrer" href="https://openstreetmap.org/copyright">© OpenStreetMap</a> <a href="https://docs.3dbag.nl/en/copyright/" target="_blank" rel="noreferrer">© 3D BAG</a> <a href="https://kadaster.nl/" target="_blank" rel="noreferrer">© Kadaster</a> <a href="https://spatial-instruments.com/" target="_blank" rel="noreferrer">© Spatial</a></div>
            </div>
            <div className={`right ${workspace === workspaceEnum.documentation && "hidden"}`}>
                <User user={user} onLogout={() => { ServiceManager.sceneManager.disposeScene(); setBuildings([]); setProjects([]); setSites([]); setCurrentBuilding(undefined); }}></User>

                {currentProject && drawState === drawStateEnum.project && <ProjectConf currentProject={currentProject} onChangeProject={editProject}></ProjectConf>}
                {currentSite && drawState === drawStateEnum.site && <SiteConf
                    changingContextVisibility={changingContextVisibility}
                    siteAddressFromModal={siteAddress}
                    loadedFeatures={loadedFeatures}
                    currentSite={currentSite}
                    pickedContextItem={pickedContextItem}
                    hoveredContextItem={hoveredContextItem}
                    pickingParcels={pickingParcels}
                    drawingParcel={drawingParcel}
                    editingUnderlay={editingUnderlay}
                    setLoadedFeatures={setLoadedFeatures}
                    addSpinner={addSpinner}
                    removeSpinner={removeSpinner}
                    onChangeSite={editSite}
                    setPickingParcels={setPickingParcels}
                    setDrawingParcel={setDrawingParcel}
                    setEditingUnderlay={setEditingUnderlay}
                ></SiteConf>}
                {currentBuilding && drawState === drawStateEnum.building && <BuildingConf
                    setBuildingInitProps={setBuildingInitProps}
                    setOpenBuildingTypeModal={setOpenBuildingTypeModal}
                    buildingScale={buildingScaledDimensions}
                    currentBuilding={currentBuilding}
                    previousBuildingDimensions={previousBuildingDimensions}
                    sectionJson={section}
                    setPreviousBuildingDimensions={setPreviousBuildingDimensions}
                    initProps={{ unitWishList: buildingInitProps.unitWishList.map(s => ({ type: s.type, percentage: s.percentage, uiName: s.uiName })) }} onChangeBuilding={editBuilding}></BuildingConf>}
            </div>
            <BuildingInitialPropertiesDialog unitsJSON={section.units} open={openBuildingTypeModal} setOpen={setOpenBuildingTypeModal} buildingInitProps={buildingInitProps} setBuildingInitProps={setBuildingInitProps} />
            <ProjectDialog open={openNewProjectModal} setOpen={setOpenNewProjectModal} setProjectName={setProjectName} />
            <SiteDialog open={openNewSiteModal} setOpen={setOpenNewSiteModal} setSiteAddress={setSiteAddress} />
        </div>
    ) : null
};

export default Confi;

